import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { object, bool } from 'prop-types';
import DataTable from 'library/components/data-table';
import { TableDataProvider } from 'library/components/data-table/components/data-provider';
import { resolve } from 'library/helpers/path-resolver';
import hasHoc from 'util/hoc/has-hoc';
import { OrgApprovalStatus } from 'apps/journey/pages/professional-center/professionals/components/org-approval-status';
import { capitalize } from 'library/helpers/text';
import { BodyText } from 'library/components/text-elements/text';
import { OrgApprovalOptions } from 'apps/journey/pages/professional-center/professionals/components/org-approval-options';
import {
  OrgTimestamp,
  getLastActionTimestamp,
} from 'apps/journey/pages/professional-center/professionals/components/org-timestamp';
import { ViewProfile } from '../view-profile';
import { pluralize } from 'library/helpers/text';

const ViewUserProfile = (record) => () => <ViewProfile data={record} />;

const StyledTableWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 3em;

  table {
    box-shadow: none;
  }
`;

const Role = ({ userRecord, individuals }) =>
  userRecord?.journey_role ? (
    <span key={`role_${userRecord.uuid}`}>
      {capitalize(userRecord.journey_role.replace('_', ' '))}
    </span>
  ) : (
    <BodyText>{individuals ? 'Individual' : 'Professional'}</BodyText>
  );

const OrganizationsSubTable = ({ isCollapsed, record, individuals }) => {
  const orgs = resolve(record, 'organizations', []).map((org) => ({
    ...org,
    last_approval_change: getLastActionTimestamp(org),
  }));
  const [checked, setChecked] = useState([]);
  const handleCheck = (id) => {
    if (checked.some((x) => x === id)) {
      setChecked(checked.filter((x) => x !== id));
    } else {
      setChecked([...checked, id]);
    }
  };
  const handleCheckAll = () => {
    if (checked.length > 0) {
      setChecked([]);
    } else {
      setChecked(orgs.map((org) => org.id));
    }
  };

  const ORG_COLUMNS = useMemo(
    () =>
      individuals
        ? [
            {
              attr: 'name',
              title: `${orgs.length} ${pluralize('Organization', orgs.length)}`,
            },
            {
              attr: 'journey_role',
              title: `Role`,
              component: Role,
            },
          ]
        : [
            {
              attr: 'name',
              title: `${orgs.length} ${pluralize('Organization', orgs.length)}`,
            },
            {
              attr: 'journey_role',
              title: `Role`,
              component: Role,
            },
            {
              title: 'Approval Status',
              attr: 'approval_status',
              component: hasHoc(OrgApprovalStatus, ['withConfig']),
              overflow: 'visible',
              order: 6,
              show: true,
            },
            {
              title: 'Last Approval Change Made',
              attr: 'last_approval_change',
              component: OrgTimestamp,
              order: 7,
              show: true,
            },
          ],
    [orgs, individuals]
  );

  const ViewUserComponent = useMemo(() => ViewUserProfile(record), []);
  const OrgApprovalOptionsComponent = useMemo(
    () => OrgApprovalOptions(record),
    []
  );

  const actionItems = !individuals
    ? [OrgApprovalOptionsComponent, ViewUserComponent]
    : null;

  const columnConfig = useMemo(
    () => ({
      columns: ORG_COLUMNS.reduce((prev, current) => {
        if (current.component) {
          const Component = current.component;
          prev[current.attr] = ({ record: orgRecord }) => (
            <Component
              record={orgRecord}
              userRecord={record}
              individuals={individuals}
              {...current}
            />
          );
        }
        return prev;
      }, {}),
    }),
    []
  );

  return (
    <StyledTableWrapper>
      <TableDataProvider
        columns={ORG_COLUMNS}
        records={orgs}
        setSelectedRecords={handleCheckAll}
        toggleRecord={handleCheck}
        checkable={true}
        selectedRecords={checked}
        actionRowItems={!individuals}
      >
        <DataTable
          columns={ORG_COLUMNS}
          data={orgs}
          checkable={true}
          selectedRecords={checked}
          config={columnConfig}
          actionItems={actionItems}
        />
      </TableDataProvider>
    </StyledTableWrapper>
  );
};

OrganizationsSubTable.propTypes = {
  record: object,
  individuals: bool,
  isCollapsed: bool,
};

export default OrganizationsSubTable;
